import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import styles from "./equipmentWeekGrid.module.css"
import BookingDetailsModal from "pagesV2/pro/clients/ClientDetails/ClientDetailsTabs/BookingTab/BookingTable/Modals/BookingDetails"
import { useCalendarJournalNoteDates } from "pagesV2/pro/Calendar/hooks/useCalendarSelectors"
import Journal from "pagesV2/pro/Calendar/Modals/Journal"
import useActiveGuide from "hooks/useActiveClient"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import useGridVisibility from "pagesV2/pro/Calendar/hooks/useGridVisibility"
import useGridModals from "pagesV2/pro/Calendar/hooks/useGridModals"
import useEquipmentGridData from "pagesV2/pro/Calendar/hooks/useEquipmentGridData"
import GridHeaderCell from "./GridHeaderCell"
import GridBodyRows from "./GridBodyRows"

const getJournalPermission = (journalNoteDates, dayStr, canView, canEdit, canCreate) => {
  const hasNote = journalNoteDates?.includes(dayStr)
  return hasNote ? canView || canEdit : canCreate
}

const EquipmentWeekGrid = ({ calendarDays, events, equipmentIds, categoryIds }) => {
  const { t } = useTranslation()
  const { getVisibleCount, showMore, showLess, maxVisible } = useGridVisibility()
  const modals = useGridModals()
  const journalNoteDates = useCalendarJournalNoteDates()
  const { isActive: active } = useActiveGuide()
  const { hasPermission } = usePermissions()
  const canViewJournal = hasPermission(MODULES.CALENDAR, ACTIONS.VIEW_JOURNAL)
  const canCreateJournal = hasPermission(MODULES.CALENDAR, ACTIONS.CREATE_JOURNAL)
  const canEditJournal = hasPermission(MODULES.CALENDAR, ACTIONS.EDIT_JOURNAL)
  const weekStartDate = calendarDays?.[0]?.format("YYYY-MM-DD")
  const weekEndDate = calendarDays?.at(-1)?.format("YYYY-MM-DD")

  const { equipmentList, showUnassigned, unassignedEventsByDay, eventsByEquipmentAndDay } = useEquipmentGridData(calendarDays, events, equipmentIds, categoryIds)

  const bodyRows = [
    ...(showUnassigned ? [{ key: "unassigned", label: t("calendar.unassigned"), eventsByDay: unassignedEventsByDay }] : []),
    ...equipmentList.map((eq) => ({ key: String(eq.id), label: eq.name, eventsByDay: eventsByEquipmentAndDay[eq.name] || {} })),
  ]

  const isEmpty = equipmentList.length === 0 && !showUnassigned
  const emptyRows = Array.from({ length: 5 }, (_, i) => (
    <tr key={`empty-${i}`} className={styles.equipmentWeekRow}>
      <td className={styles.equipmentWeekEquipmentCell} />
      {calendarDays.map((day) => <td key={day.format("YYYY-MM-DD")} className={styles.equipmentWeekEventCell} />)}
    </tr>
  ))

  return (
    <div className={styles.equipmentWeekGrid}>
      <table className={styles.equipmentWeekTable}>
        <thead>
          <tr className={styles.equipmentWeekHeaderRow}>
            <th className={styles.equipmentWeekHeaderCell}>{t("calendar.equipment_label")}</th>
            {calendarDays.map((day) => {
              const dayStr = day.format("YYYY-MM-DD")
              return (
                <GridHeaderCell
                  key={dayStr}
                  day={day}
                  dayStr={dayStr}
                  journalNoteDates={journalNoteDates}
                  hasJournalPermission={getJournalPermission(journalNoteDates, dayStr, canViewJournal, canEditJournal, canCreateJournal)}
                  isActive={active}
                  onJournalClick={modals.openJournalModal}
                  thClassName={styles.equipmentWeekHeaderCell}
                />
              )
            })}
          </tr>
        </thead>
        <tbody className={styles.equipmentWeekBody}>
          {isEmpty ? emptyRows : (
            <GridBodyRows
              rows={bodyRows}
              calendarDays={calendarDays}
              getVisibleCount={getVisibleCount}
              maxVisible={maxVisible}
              showMore={showMore}
              showLess={showLess}
              openBookingModal={modals.openBookingModal}
              rowClassName={styles.equipmentWeekRow}
              nameCellClassName={styles.equipmentWeekEquipmentCell}
              eventCellClassName={styles.equipmentWeekEventCell}
              eventCellInnerClassName={styles.equipmentWeekEventCellInner}
            />
          )}
        </tbody>
      </table>
      {modals.isBookingModalOpen && (
        <BookingDetailsModal
          open={modals.isBookingModalOpen}
          handleOnCancel={modals.closeBookingModal}
          bookingUuid={modals.selectedBookingId}
          onCancelSuccess={modals.handleBookingCancelSuccess}
        />
      )}
      {modals.isJournalModalOpen && (
        <Journal
          open={modals.isJournalModalOpen}
          setOpen={modals.closeJournalModal}
          onCancel={modals.closeJournalModal}
          selectedDate={modals.selectedJournalDate}
          startDate={weekStartDate}
          endDate={weekEndDate}
          range="week"
        />
      )}
    </div>
  )
}

EquipmentWeekGrid.propTypes = {
  calendarDays: PropTypes.array.isRequired,
  events: PropTypes.array.isRequired,
  equipmentIds: PropTypes.array,
  categoryIds: PropTypes.array,
}

export default EquipmentWeekGrid
